import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getRoleId } from 'features/staff-and-rooms/helpers';
import { TIMING_CATEGORIES_NAME, ROOM_STATUS } from 'shared/constants';
import { Accordion } from 'shared/components';
import { StaffCard } from 'features/staff-and-rooms';
import { getPersonnelRoles, getPersonnelRolesName, getRoomTimingName } from 'shared/helpers';
import PencilIcon from 'assets/svgs/pencilIcon.svg';
import GarbageIcon from 'assets/svgs/garbageIcon.svg';
import StaffList from '../StaffList';

import './RoomStaff.scss';

const RoomStaff = (props) => {
  const { t } = useTranslation();
  const personnelRoles = getPersonnelRoles();
  const personnelRolesName = getPersonnelRolesName();
  const ROOM_SUPERVISOR_ID = getRoleId(props.roles, personnelRoles.ROOM_SUPERVISOR);
  const PROCTOR_ID = getRoleId(props.roles, personnelRoles.PROCTOR);

  const proctorNumber = (room) => {
    // Capacity isn't known yet (e.g. a draft room saved without one) — the proctor count
    // can't be computed, so return null rather than letting `null <= 35` silently coerce to
    // a legitimate-looking "0 proctors needed".
    if (room.capacity == null) {
      return null;
    }

    if (room.timing === TIMING_CATEGORIES_NAME.extended) {
      return Math.floor((room.capacity - 1) / 20);
    } else {
      switch (true) {
        case room.capacity <= 35:
          return 0;
        case room.capacity <= 70:
          return 1;
        case room.capacity <= 105:
          return 2;
        case room.capacity <= 140:
          return 3;
        case room.capacity <= 175:
          return 4;
        case room.capacity <= 210:
          return 5;
        default:
          return;
      }
    }
  };

  const findStaff = (staff, id) => staff?.find((s) => s.roleId === id);

  const renderProctor = (room) => {
    const proctors = props.staffByRooms[room.id]
      ? props.staffByRooms[room.id].filter((s) => s.roleId === PROCTOR_ID)
      : [];
    const proctorsNo = proctorNumber(room);
    // proctorsNo is null when capacity isn't known yet — fall back to the number of
    // proctors already assigned so we neither add fictitious empty slots nor let
    // `null - proctors.length` go negative and blow up Array.from below.
    const countProctors = props.unlimitedRoomProctorsFlag
      ? proctors.length
      : proctorsNo ?? proctors.length;

    return (
      <StaffList
        list={[
          ...proctors,
          ...Array.from({ length: countProctors - proctors.length }, () => {
            return {};
          }),
        ]}
        roleTitle={personnelRolesName.PROCTOR}
        roleId={PROCTOR_ID}
        room={room}
        onOpenModal={props.onOpenModal}
        onRemoveStaffRole={props.onRemoveStaffRole}
        showDelete={props.unlimitedRoomProctorsFlag}
        addLabel="staffManagement.addProctorLabel"
      />
    );
  };

  const renderRoomActions = (room) => {
    if (props.seatingChartRequiredFlag) {
      return (
        <div className="d-flex justify-content-end">
          <button className="btn-link edit-button" onClick={() => props.openRoomModal(room)}>
            {t('staffAndRoomsManagement.editSeeRoomDetails')}
          </button>
        </div>
      );
    } else {
      const isDeleteDisabled = props.roomDeleteStatus?.[room.id] === true;

      return (
        <div className="d-flex justify-content-end gap-2">
          <button
            className="pl-0 btn-link text-pacific-blue fw-bold"
            onClick={() => props.onEditRoom(room.id)}>
            <span>
              <PencilIcon />
              {t('roomBuilder.editInformationLabel')}
            </span>
          </button>
          <button
            className="btn-link remove-button fw-bold"
            onClick={() => props.onDeleteRoom(room.id)}
            disabled={isDeleteDisabled}>
            <span>
              <GarbageIcon />
              {t('roomBuilder.deleteRoomLabel')}
            </span>
          </button>
        </div>
      );
    }
  };

  return (
    <div className="rooms-staff-list">
      <Accordion
        startPosition={props.startPosition}
        onOpen={(index) => props.onRoomOpened && props.onRoomOpened(props.rooms[index]?.id)}
      >
        {props.rooms.map((room) => (
          <div
            key={`room-${room.id}`}
            data-trigger={
              <div className="room-details-container">
                <span
                  className={`room-${
                    room.confirmMaterialDateTime ? ROOM_STATUS.submitted : room.status
                  }`}>
                  <span className="text-bold">
                    {room.name}
                    <span className="no-wrap">
                      {' '}
                      ({room.capacity != null ? `${room.capacity} seats` : t('common.labels.notSpecified')})
                    </span>
                  </span>
                </span>
                <div className="roomtype">
                  <span className="label-box-radius">
                    {room.roomType?.name || t('testCenter.roomDashboard.noTypeLabel')}
                  </span>
                  {room.timing && (
                    <span className="label-box-radius">
                      {getRoomTimingName(room.timing, props.programCode, props.mappedRoomTiming)}
                    </span>
                  )}
                  {room.roomType && (
                    <span className="label-box-radius">
                      {room.roomType.onlineFlag
                        ? t('common.labels.online')
                        : t('common.labels.paper')}
                    </span>
                  )}
                </div>
              </div>
            }>
            <div>
              <StaffCard
                staff={findStaff(props.staffByRooms[room.id], ROOM_SUPERVISOR_ID)}
                roleId={ROOM_SUPERVISOR_ID}
                room={room}
                onOpenModal={props.onOpenModal}
                title={personnelRolesName.ROOM_SUPERVISOR}
              />
              {renderProctor(room)}
              <div className="delimiter"></div>
              <div className="d-flex justify-content-end">{renderRoomActions(room)}</div>
            </div>
          </div>
        ))}
      </Accordion>
    </div>
  );
};

export default RoomStaff;

RoomStaff.propTypes = {
  rooms: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onOpenModal: PropTypes.func.isRequired,
  onRemoveStaffRole: PropTypes.func.isRequired,
  unlimitedRoomProctorsFlag: PropTypes.bool.isRequired,
  staffByRooms: PropTypes.shape({}).isRequired,
  seatingChartRequiredFlag: PropTypes.bool,
  openRoomModal: PropTypes.func,
  roomDeleteStatus: PropTypes.shape({}),
  onEditRoom: PropTypes.func,
  onDeleteRoom: PropTypes.func,
  mappedRoomTiming: PropTypes.shape({}),
  startPosition: PropTypes.number,
  onRoomOpened: PropTypes.func,
};
